import { EventFilter, RequestOptions } from "@key-telematics/fleet-api-client";
import { EventMasker } from "@key-telematics/fleet-ui-shared";

interface ListResponse<T = any> {
    items: T[]
}

interface Event {
    eventClass: string;
    eventType: string;
    details?: { [key: string]: any }
}

interface EventFilterItem {
    filter?: EventFilter;
}

interface EntityWithEventFilterItems {
    items?: EventFilterItem[];
}

export function eventListResponseMask<T extends ListResponse<Event>>(context: any, fn: (...args: any[]) => Promise<T>) {
    return async function(): Promise<T> {
        const res: T = await fn.apply(context, arguments);
    
        res.items = res.items.map(item => EventMasker.fromBackendEvent(item));

        return res;
    }
}

export function eventResponseMask<T extends Event>(context: any, fn: (...args: any[]) => Promise<T>) {
    return async function(): Promise<T> {
        const res: T = await fn.apply(context, arguments);
        return EventMasker.fromBackendEvent(res);
    }
}

export function eventFilterListResponseMask<T extends ListResponse>(context: any, fn: (...args: any[]) => Promise<T>) {
    return async function(): Promise<T> {
        const res: T = await fn.apply(context, arguments);

        for (const item of res.items || []) {
            item.filter = item.filter && EventMasker.filterFromBackend(item.filter);
        }

        return res;
    }
}

export function eventFilterResponseMask<T extends EventFilterItem>(context: any, fn: (...args: any[]) => Promise<T>) {
    return async function(): Promise<T> {
        const item = await fn.apply(context, arguments)

        if (item.filter) {
            item.filter = EventMasker.filterFromBackend(item.filter);
        }

        return item;
    }
}

export function createEventFilterRequestMask<INPUT extends EventFilterItem, OUTPUT = any>(context: any, fn: (item: INPUT, $options?: RequestOptions) => Promise<OUTPUT>) {
    return async function(item: INPUT, $options?: RequestOptions): Promise<OUTPUT> {
        let model = { ...item };

        if (model.filter) {
            model.filter = EventMasker.filterToBackend(model.filter)
        }

        const res = await fn.call(context, model, $options);

        if (res.filter) {
            res.filter = EventMasker.filterFromBackend(res.filter);
        }

        return res;
    }
}

export function updateEventFilterRequestMask<INPUT extends EventFilterItem, OUTPUT = any>(context: any, fn: (id: string, changes: INPUT, $options?: RequestOptions) => Promise<OUTPUT>) {
    return async function(id: string, changes: INPUT, $options?: RequestOptions): Promise<OUTPUT> {
        let model = { ...changes };

        if (model.filter) {
            model.filter = EventMasker.filterToBackend(model.filter)
        }

        const res = await fn.call(context, id, model, $options);

        if (res.filter) {
            res.filter = EventMasker.filterFromBackend(res.filter);
        }

        return res;
    }
}

export function entityWithEventFilterResponseMask<T extends EntityWithEventFilterItems>(context: any, fn: (...args: any[]) => Promise<T>) {
    return async function(): Promise<T> {
        const res: T = await fn.apply(context, arguments)

        for (const item of res.items || []) {
            if (item.filter) {
                item.filter = EventMasker.filterFromBackend(item.filter);
            }
        }

        return res;
    }
}

export function createEntityWithEventFilterRequestMask<INPUT extends EntityWithEventFilterItems, OUTPUT = any>(context: any, fn: (entity: INPUT, $options?: RequestOptions) => Promise<OUTPUT>) {
    return async function(entity: INPUT, $options?: RequestOptions): Promise<OUTPUT> {
        for (const item of entity.items || []){
            if (item.filter) {
                item.filter = EventMasker.filterToBackend(item.filter)
            }
        }

        const res = await fn.call(context, entity, $options);

        for (const item of res.items || []){
            if (item.filter) {
                item.filter = EventMasker.filterFromBackend(item.filter);
            }
        }

        return res;
    }
}

export function updateEntityWithEventFilterRequestMask<INPUT extends EntityWithEventFilterItems, OUTPUT = any>(context: any, fn: (id: string, changes: INPUT, $options?: RequestOptions) => Promise<OUTPUT>) {
    return async function(id: string, changes: INPUT, $options?: RequestOptions): Promise<OUTPUT> {
        for (const item of changes.items || []){
            if (item.filter) {
                item.filter = EventMasker.filterToBackend(item.filter)
            }
        }

        const res = await fn.call(context, id, changes, $options);

        for (const item of res.items || []){
            if (item.filter) {
                item.filter = EventMasker.filterFromBackend(item.filter);
            }
        }

        return res;
    }
}